// @ts-check
import React from "react"
import {
  isEmptyLikeSetMemoryPayload,
  SET_MEMORY_EMPTY_OUTPUT_NOTICE,
  toolIdFromPart,
} from "./uiMessageParts"

/**
 * @param {import("ai").ToolUIPart | import("ai").DynamicToolUIPart} part
 * @returns {unknown}
 */
function getToolPreviewPayload(part) {
  if (!part || typeof part !== "object") return undefined
  if (
    "state" in part &&
    part.state === "output-available" &&
    "output" in part &&
    part.output !== undefined
  ) {
    return part.output
  }
  if ("input" in part && part.input !== undefined) return part.input
  return undefined
}

/**
 * @param {import("ai").UIMessage["parts"][number]} part
 * @returns {string | undefined}
 */
function toolNameForPreview(part) {
  return toolIdFromPart(part)
}

/**
 * Backend puede registrar la tool como `edit_feedback` o `update_feedback`.
 *
 * @param {string | undefined} name
 */
function isEditFeedbackToolName(name) {
  return name === "edit_feedback" || name === "update_feedback"
}

/**
 * @typedef {import("ai").ToolUIPart | import("ai").DynamicToolUIPart} ToolUIPartLike
 */

/**
 * @param {{ feedback: Record<string, unknown> }} props
 */
function ApprovalFeedbackFields({ feedback }) {
  const o = feedback
  return (
    <>
      {o.origin !== undefined ?
        <div className="approval-feedback-row">
          <span className="approval-feedback-label">Proyecto:</span>
          <span className="approval-feedback-value">{String(o.origin)}</span>
        </div>
      : null}
      {o.description !== undefined ?
        <div className="approval-feedback-row">
          <span className="approval-feedback-label">Descripción:</span>
          <span className="approval-feedback-value">
            {String(o.description)}
          </span>
        </div>
      : null}
      {o.base_time !== undefined && o.adjusted_time !== undefined ?
        <div className="approval-feedback-row">
          <span className="approval-feedback-label">Tiempo estimado:</span>
          <span className="approval-feedback-value">
            {String(o.base_time)}{" "}
            <i className="pi pi-arrow-right mx-2" aria-hidden />{" "}
            {String(o.adjusted_time)}
          </span>
        </div>
      : null}
      {o.sentiment !== undefined ?
        <div className="approval-feedback-row">
          <span className="approval-feedback-label">Sentimiento:</span>
          <span
            className={`approval-feedback-sentiment sentiment-${String(o.sentiment)}`}
          >
            {String(o.sentiment)}
          </span>
        </div>
      : null}
      {Array.isArray(o.keywords) && o.keywords.length > 0 ?
        <div className="approval-feedback-row">
          <span className="approval-feedback-label">Palabras clave:</span>
          <div className="approval-feedback-keywords">
            {o.keywords.map((kw, idx) => (
              <span key={idx} className="approval-keyword-tag">
                {String(kw)}
              </span>
            ))}
          </div>
        </div>
      : null}
    </>
  )
}

/**
 * @typedef {object} ToolApprovalParamsPreviewProps
 * @property {ToolUIPartLike} part
 */

/**
 * @param {ToolApprovalParamsPreviewProps} props
 * @returns {import("react").ReactNode}
 */
export function ToolApprovalParamsPreview({ part }) {
  const toolName = toolNameForPreview(part)
  const payload = getToolPreviewPayload(part)

  if (toolName === "set_memory") {
    if (isEmptyLikeSetMemoryPayload(payload)) {
      return (
        <p className="estimation-agent-quick-v3__set-memory-approval-notice">
          {SET_MEMORY_EMPTY_OUTPUT_NOTICE}
        </p>
      )
    }
    if (payload && typeof payload === "object") {
      const mem = /** @type {{ memory?: unknown }} */ (payload).memory
      if (Array.isArray(mem) && mem.length > 0) {
        return (
          <ul className="approval-list">
            {mem.map((item, idx) => (
              <li key={idx} className="approval-list-item">
                <i className="pi pi-plus-circle text-success mr-2" aria-hidden />
                <span>
                  {typeof item === "string" ? item : JSON.stringify(item)}
                </span>
              </li>
            ))}
          </ul>
        )
      }
    }
  }

  if (
    (toolName === "new_feedback" || toolName === "add_feedback") &&
    payload &&
    typeof payload === "object"
  ) {
    const fb = /** @type {Record<string, unknown>} */ (payload).feedback
    if (fb && typeof fb === "object") {
      const o = /** @type {Record<string, unknown>} */ (fb)
      return (
        <div className="approval-feedback-card">
          <ApprovalFeedbackFields feedback={o} />
        </div>
      )
    }
  }

  if (isEditFeedbackToolName(toolName) && payload && typeof payload === "object") {
    const o = /** @type {Record<string, unknown>} */ (payload)
    const nested = o.feedback
    if (nested && typeof nested === "object") {
      const id = o.feedback_id ?? o.id
      const fb = /** @type {Record<string, unknown>} */ (nested)
      return (
        <div className="approval-feedback-card">
          {id !== undefined ?
            <div className="approval-feedback-row">
              <span className="approval-feedback-label">ID:</span>
              <span className="approval-feedback-value">{String(id)}</span>
            </div>
          : null}
          <ApprovalFeedbackFields feedback={fb} />
        </div>
      )
    }

    const entries = Object.entries(o).filter(
      ([k]) => k !== "id" && k !== "feedback_id"
    )
    return (
      <div className="approval-feedback-card">
        {(o.id !== undefined || o.feedback_id !== undefined) && (
          <div className="approval-feedback-row">
            <span className="approval-feedback-label">ID:</span>
            <span className="approval-feedback-value">
              {String(o.feedback_id ?? o.id)}
            </span>
          </div>
        )}
        {entries.map(([k, v]) => (
          <div key={k} className="approval-feedback-row">
            <span className="approval-feedback-label">{k}:</span>
            <span className="approval-feedback-value">
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (toolName === "delete_feedback" && payload && typeof payload === "object") {
    const o = /** @type {Record<string, unknown>} */ (payload)
    const id = o.id ?? o.feedback_id
    if (id !== undefined) {
      return (
        <p className="estimation-agent-quick-v3__delete-feedback-approval-notice">
          Eliminar la retroalimentación seleccionada
        </p>
      )
    }
  }

  return (
    <pre className="approval-json">
      {payload === undefined ?
        "—"
      : JSON.stringify(payload, null, 2)}
    </pre>
  )
}
